/**
 * Générateur — Calculer des périmètres de figures composées
 * Attendu ID : 642 | Niveau : 6ème | Domaine : Grandeurs et mesures — Les longueurs
 *
 * Types générés : qcm_unique, texte, relier, tableau_vf, texte_trou_drag, texte_trou_saisie
 * Labels de référence :
 *   - Calculer le périmètre d'une figure avec un demi-cercle et des segments
 *   - Calculer le périmètre d'une figure avec un quart de cercle et des segments
 *   - Calculer le périmètre d'une figure avec un cercle complet et des segments
 * Variantes estimées : ~500+
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 642;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEMI = 'Calculer le périmètre d\'une figure avec un demi-cercle et des segments';
const L_QUART = 'Calculer le périmètre d\'une figure avec un quart de cercle et des segments';
const L_COMPOSE = 'Calculer le périmètre d\'une figure composée';

// ─── Helpers numériques ───
const arrCent = (n) => Math.round(n * 100) / 100;
const fmtFr = (n) => String(n).replace('.', ',');
const toutDistinct = (arr) => new Set(arr.map(String)).size === arr.length;

// ─── Pool de figures composées ───

/**
 * Chaque figure retourne :
 *   { label, descCourte, perimetre, segments, arcs, svgFn, piegeFns, labelType }
 */

const FIGURE_TYPES = [
    // Type 1 : Rectangle surmonté d'un demi-cercle (D = largeur)
    {
        id: 'rect_demi',
        labelType: L_DEMI,
        gen: () => {
            const L = randInt(5, 15); // largeur (= diamètre du demi-cercle)
            const H = randInt(5, 15); // hauteur du rectangle
            if (L === H) return null; // éviter carré parfait (ambiguïté visuelle)
            const demiArc = arrCent(Math.PI * L / 2);
            const perimetre = arrCent(2 * H + L + demiArc);
            const descCourte = `Rectangle ${L}×${H} + demi-cercle (D = ${L})`;

            // Proportions RÉELLES (revue 2026-07-31) : l'ancienne largeur fixe
            // (150 px) contredisait les cotes un tirage sur deux (doctrine
            // 614/969). Chaque étiquette est PLAQUÉE contre le côté qu'elle
            // mesure (retour utilisateur : « 14 cm » flottait au centre du
            // rectangle, illisible) : L sous la base, H à gauche du côté.
            // Échelle 120 px pour garder la marge haute (arc + grand rectangle).
            const kEch = 120 / Math.max(L, H);
            const wPx = Math.round(kEch * L);
            const hPx = Math.round(kEch * H);
            const x0 = Math.round((220 - wPx) / 2);
            const yBase = 185;                 // base ; l'étiquette L vit dessous
            const yRect = yBase - hPx;
            const rArc = wPx / 2;
            const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='220' height='210' viewBox='0 0 220 210'>`
                + `<rect x='${x0}' y='${yRect}' width='${wPx}' height='${hPx}' fill='none' stroke='currentColor' stroke-width='2'/>`
                + `<path d='M ${x0} ${yRect} A ${rArc} ${rArc} 0 0 1 ${x0 + wPx} ${yRect}' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
                + `<text x='${x0 + wPx / 2}' y='${yBase + 16}' text-anchor='middle' font-size='13' fill='currentColor'>${L} cm</text>`
                + `<text x='${x0 - 6}' y='${yRect + hPx / 2 + 5}' text-anchor='end' font-size='13' fill='currentColor'>${H} cm</text>`
                + `</svg>`;

            const pieges = [
                arrCent(2 * H + L + Math.PI * L),         // cercle complet au lieu du demi
                arrCent(2 * H + 2 * L),                   // seulement les segments (oubli arc)
                arrCent(2 * H + 2 * L + Math.PI * L / 2), // côté en trop (haut rect + arc)
            ];

            return { descCourte, perimetre, svg, pieges, labelType: L_DEMI, L, H };
        },
    },
    // Type 2 : VRAI quart de disque (revue 2026-07-31). L'ancien « carré dont
    // un côté est remplacé par un quart de cercle de rayon C » était
    // géométriquement IMPOSSIBLE : la corde d'un quart de rayon C vaut C√2,
    // jamais C — l'arc dessiné (60°) ne correspondait ni au quart annoncé ni
    // au demi compatible, et les clés ne tombaient juste que par coïncidence
    // (quart de rayon C et demi de diamètre C ont la même longueur 2πC/4).
    // Désormais : part de camembert — 2 rayons perpendiculaires + arc CENTRÉ
    // sur l'angle droit. Figure, énoncé et calcul racontent la même chose.
    {
        id: 'carre_quart',
        labelType: L_QUART,
        gen: () => {
            const C = randInt(5, 18);
            const quartArc = arrCent(2 * Math.PI * C / 4);
            const perimetre = arrCent(2 * C + quartArc);
            const descCourte = `Quart de disque de rayon ${C}`;

            const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'>`
                + `<line x1='45' y1='30' x2='185' y2='30' stroke='currentColor' stroke-width='2'/>`
                + `<line x1='45' y1='30' x2='45' y2='170' stroke='currentColor' stroke-width='2'/>`
                + `<path d='M 185 30 A 140 140 0 0 1 45 170' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
                + `<text x='115' y='25' text-anchor='middle' font-size='13' fill='currentColor'>${C} cm</text>`
                + `<text x='39' y='105' text-anchor='end' font-size='13' fill='currentColor'>${C} cm</text>`
                + `</svg>`;

            const pieges = [
                arrCent(3 * C + quartArc),                  // côté fantôme (3 côtés au lieu de 2)
                arrCent(2 * C + Math.PI * C),               // demi-cercle au lieu du quart
                arrCent(2 * C),                             // oubli de l'arc
            ];

            return { descCourte, perimetre, svg, pieges, labelType: L_QUART, C };
        },
    },
    // Type 3 : Stade (rectangle + 2 demi-cercles aux extrémités, D = largeur)
    {
        id: 'stade',
        labelType: L_COMPOSE,
        gen: () => {
            const Lg = randInt(8, 18); // longueur des droites
            const La = randInt(4, 12); // largeur (= diamètre des demi-cercles)
            if (Lg <= La) return null; // le stade doit être plus long que large
            const cercleFull = arrCent(Math.PI * La);
            const perimetre = arrCent(2 * Lg + cercleFull);
            const descCourte = `Stade ${Lg}×${La} (2 demi-cercles, D = ${La})`;

            const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='280' height='140' viewBox='0 0 280 140'>`
                + `<line x1='70' y1='20' x2='210' y2='20' stroke='currentColor' stroke-width='2'/>`
                + `<line x1='70' y1='120' x2='210' y2='120' stroke='currentColor' stroke-width='2'/>`
                + `<path d='M 70 20 A 50 50 0 0 0 70 120' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
                + `<path d='M 210 20 A 50 50 0 0 1 210 120' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
                + `<text x='140' y='15' text-anchor='middle' font-size='12' fill='currentColor'>${Lg} cm</text>`
                + `<text x='140' y='135' text-anchor='middle' font-size='12' fill='currentColor'>${Lg} cm</text>`
                + `<line x1='210' y1='20' x2='210' y2='120' stroke='#fb923c' stroke-width='2' stroke-dasharray='4,3'/>`
                + `<text x='202' y='74' text-anchor='end' font-size='12' fill='#fb923c'>D = ${La} cm</text>`
                + `</svg>`;

            const pieges = [
                arrCent(2 * Lg + 2 * La),                      // oubli de π (seulement segments)
                arrCent(2 * Lg),                                // oubli des arcs
                arrCent(2 * Lg + 2 * Math.PI * La),             // facteur 2 en trop (2 cercles au lieu de 2 demi)
            ];

            return { descCourte, perimetre, svg, pieges, labelType: L_COMPOSE, Lg, La };
        },
    },
    // Type 4 : Demi-cercle seul (contour = arc + diamètre)
    {
        id: 'demi_seul',
        labelType: L_DEMI,
        gen: () => {
            const R = randInt(3, 15);
            const D = 2 * R;
            const arc = arrCent(Math.PI * R);
            const perimetre = arrCent(arc + D);
            const descCourte = `Demi-cercle seul, R = ${R}`;

            const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='130' viewBox='0 0 200 130'>`
                + `<path d='M 20 100 A 80 80 0 0 1 180 100' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
                + `<line x1='20' y1='100' x2='180' y2='100' stroke='currentColor' stroke-width='2'/>`
                + `<text x='100' y='115' text-anchor='middle' font-size='13' fill='currentColor'>D = ${D} cm</text>`
                + `<circle cx='100' cy='100' r='2.5' fill='#fb923c'/>`
                + `<line x1='100' y1='100' x2='100' y2='20' stroke='#fb923c' stroke-width='2' stroke-dasharray='4,3'/>`
                + `<text x='108' y='60' text-anchor='start' font-size='13' fill='#fb923c'>R = ${R} cm</text>`
                + `</svg>`;

            const pieges = [
                arrCent(Math.PI * D),                      // périmètre du cercle complet (pas juste demi)
                arrCent(Math.PI * R + R),                  // oubli : diamètre = R au lieu de 2R
                arrCent(D),                                // seulement le diamètre (oubli de l'arc)
            ];

            return { descCourte, perimetre, svg, pieges, labelType: L_DEMI, R, D };
        },
    },
];

// Tire une figure valide avec pièges tous distincts
const tirerFigure = (typeId) => {
    const figType = typeId
        ? FIGURE_TYPES.find(f => f.id === typeId)
        : randomChoice(FIGURE_TYPES);
    for (let attempt = 0; attempt < 50; attempt++) {
        const fig = figType.gen();
        if (!fig) continue;
        if (fig.perimetre < 10) continue;
        if (!toutDistinct([fig.perimetre, ...fig.pieges])) continue;
        return fig;
    }
    // Fallback
    return figType.gen() || FIGURE_TYPES[0].gen();
};

// ─── Générateurs par type ───

/** Convertit un SVG brut en data URL + element image. */
const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

const genComposeQcm = () => {
    const fig = tirerFigure();
    const texte = `<p>Calcule le périmètre de la figure ci-dessous. Arrondis au <strong>centième</strong> de cm. Utilise la touche π de ta calculatrice.</p>{{element:0}}`;

    const correctStr = `${fmtFr(fig.perimetre)} cm`;
    const choix = [correctStr, ...fig.pieges.map(p => `${fmtFr(p)} cm`)];

    const payload = makeQcm(choix, correctStr, texte, { ...opts(fig.labelType), elements_additionnels: svgToElement(fig.svg) });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'perimetre_compose';
    return payload;
};

const genComposeSaisie = () => {
    const fig = tirerFigure();
    const texte = `<p>Calcule le périmètre de la figure ci-dessous. Arrondis au <strong>centième</strong> de cm. Utilise la touche π de ta calculatrice.</p>{{element:0}}`;

    const payload = makeSaisie(fmtFr(fig.perimetre), texte, { ...opts(fig.labelType), suffixe: 'cm', elements_additionnels: svgToElement(fig.svg) });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'perimetre_compose';
    return payload;
};

const genComposeDrag = () => {
    // Filtre les distracteurs qui sont identiques aux bonnes réponses
    const filtrerDistracteurs = (phrase, distracteurs) => {
        const trous = [...phrase.matchAll(/\{\{(.+?)\}\}/g)].map(m => m[1]);
        return distracteurs.filter(d => !trous.includes(d));
    };

    // CORRECTIF : la figure est tirée D'ABORD, puis la phrase à trous est construite à
    // partir des dimensions RÉELLES de la figure (comme genComposeSaisieTrou). L'ancien
    // code tirait la phrase (avec ses propres Lg/La) et la figure INDÉPENDAMMENT
    // → figure et énoncé incompatibles à chaque tirage. Dériver la phrase de la figure
    // aligne aussi la contrainte du stade (Lg > La, garantie par FIGURE_TYPES).
    const buildPhrase = (fig) => {
        if (fig.L !== undefined && fig.H !== undefined) {
            // rect_demi : 2 côtés (H), 1 base (L), 1 demi-cercle de diamètre L
            const phrase = `Le contour est composé de {{2}} côtés de {{${fig.H}}} cm, {{1}} côté de {{${fig.L}}} cm (la base) et {{1 demi-cercle}} de diamètre {{${fig.L}}} cm.`;
            return { phrase, rawDist: ['1 cercle complet', '3', String(fig.L + fig.H)], label: L_DEMI };
        }
        if (fig.C !== undefined) {
            // carre_quart (vrai quart de disque) : 2 rayons + 1 quart de cercle
            const phrase = `Le contour est composé de {{2}} côtés (les rayons) de {{${fig.C}}} cm et {{1 quart de cercle}} de rayon {{${fig.C}}} cm.`;
            return { phrase, rawDist: ['3', '1 demi-cercle', String(fig.C * 2)], label: L_QUART };
        }
        if (fig.Lg !== undefined) {
            // stade : 2 lignes droites (Lg), 2 demi-cercles de diamètre La
            const phrase = `Le contour est composé de {{2}} lignes droites de {{${fig.Lg}}} cm et de {{2 demi-cercles}} de diamètre {{${fig.La}}} cm.`;
            return { phrase, rawDist: ['1 cercle complet', '4', String(fig.Lg + fig.La)], label: L_COMPOSE };
        }
        // demi_seul : 1 diamètre (D) + 1 demi-cercle de diamètre D
        const phrase = `Le contour est composé de {{1}} segment de {{${fig.D}}} cm (le diamètre) et {{1 demi-cercle}} de diamètre {{${fig.D}}} cm.`;
        return { phrase, rawDist: ['1 cercle complet', '2', String(fig.R)], label: L_DEMI };
    };

    for (let attempt = 0; attempt < 50; attempt++) {
        const fig = tirerFigure();
        const { phrase, rawDist, label } = buildPhrase(fig);
        const distracteurs = filtrerDistracteurs(phrase, rawDist);
        if (distracteurs.length < 2) continue;
        const texte = `<p>Complète la décomposition du périmètre de la figure ci-dessous.</p>{{element:0}}`;
        const payload = makeTexteTrou(phrase, texte, { ...opts(label), distracteurs, elements_additionnels: svgToElement(fig.svg) });
        payload._meta.generatorKey = 'perimetre_compose';
        return payload;
    }

    // Fallback sûr : figure d'abord, phrase dérivée
    const fig = tirerFigure();
    const { phrase, rawDist, label } = buildPhrase(fig);
    const texte = `<p>Complète la décomposition du périmètre de la figure ci-dessous.</p>{{element:0}}`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(label), distracteurs: filtrerDistracteurs(phrase, rawDist), elements_additionnels: svgToElement(fig.svg) });
    payload._meta.generatorKey = 'perimetre_compose';
    return payload;
};

const genComposeSaisieTrou = () => {
    // Utilise tirerFigure pour avoir le SVG + les données
    const fig = tirerFigure();
    let phrase, label;

    if (fig.L !== undefined && fig.H !== undefined) {
        // rect_demi
        const demiArc = arrCent(Math.PI * fig.L / 2);
        const segments = 2 * fig.H + fig.L;
        phrase = `Longueur du demi-cercle = π × {{${fig.L}}} ÷ 2 ≈ {{${fmtFr(demiArc)}}} cm.\n`
            + `Segments : 2 × {{${fig.H}}} + {{${fig.L}}} = {{${segments}}} cm.\n`
            + `Périmètre total = demi-cercle + segments ≈ {{${fmtFr(fig.perimetre)}}} cm.`;
        label = L_DEMI;
    } else if (fig.C !== undefined) {
        // carre_quart (vrai quart de disque : 2 rayons + arc)
        const quartArc = arrCent(2 * Math.PI * fig.C / 4);
        const segments = 2 * fig.C;
        phrase = `Longueur du quart de cercle = 2 × π × {{${fig.C}}} ÷ 4 ≈ {{${fmtFr(quartArc)}}} cm.\n`
            + `Segments : 2 × {{${fig.C}}} = {{${segments}}} cm.\n`
            + `Périmètre total = quart de cercle + segments ≈ {{${fmtFr(fig.perimetre)}}} cm.`;
        label = L_QUART;
    } else if (fig.Lg !== undefined) {
        // stade
        const cercleFull = arrCent(Math.PI * fig.La);
        const droites = 2 * fig.Lg;
        phrase = `Longueur des 2 demi-cercles = π × {{${fig.La}}} ≈ {{${fmtFr(cercleFull)}}} cm.\n`
            + `Lignes droites : 2 × {{${fig.Lg}}} = {{${droites}}} cm.\n`
            + `Périmètre total = demi-cercles + lignes droites ≈ {{${fmtFr(fig.perimetre)}}} cm.`;
        label = L_COMPOSE;
    } else {
        // demi_seul — forme π × D ÷ 2 (revue 2026-07-31) : l'ancienne forme
        // π × {{R}} sans ÷ 2 était juste mais jamais enseignée ailleurs — la
        // figure affiche R ET D, l'élève appliquant la méthode du gen (« tour
        // entier ÷ 2 ») cherchait D et un ÷ 2 absents. La ligne « Diamètre =
        // {{D}} » saute : recopie pure une fois D dans la formule.
        const R = Math.round(fig.perimetre / (Math.PI + 2)); // retrouver R depuis périmètre
        const D = 2 * R;
        const arc = arrCent(Math.PI * R);
        phrase = `Longueur de l'arc = π × {{${D}}} ÷ 2 ≈ {{${fmtFr(arc)}}} cm.\n`
            + `Périmètre total = arc + diamètre ≈ {{${fmtFr(fig.perimetre)}}} cm.`;
        label = L_DEMI;
    }

    const texte = `<p>Complète le calcul du périmètre de la figure ci-dessous. Arrondis au <strong>centième</strong>. Utilise la touche π de ta calculatrice.</p>{{element:0}}`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(label), mode: 'saisie', elements_additionnels: svgToElement(fig.svg) });
    // Calculatrice AUTORISÉE (revue 2026-07-31, convention touche π du gen).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'perimetre_compose';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    perimetre_compose_qcm_unique: genComposeQcm,
    perimetre_compose_texte: genComposeSaisie,
    perimetre_compose_texte_trou_drag: genComposeDrag,
    perimetre_compose_texte_trou_saisie: genComposeSaisieTrou,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'perimetre_compose', label: L_COMPOSE, formatsList: buildFormats('perimetre_compose') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
